import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Sparkles,
  MessagesSquare,
  Contact2,
  ShoppingBag,
  CalendarCheck,
  CalendarClock,
  Users,
  CreditCard,
  BarChart3,
  Truck,
  Receipt,
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Store,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Logo } from '../shared/Logo';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { prefetchRoute } from '../../lib/routePrefetch';
import { cn } from '../../lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/analytics', label: 'Analytics', icon: BarChart3 },
    ],
  },
  {
    label: 'Sell',
    items: [
      { to: '/catalog', label: 'Catalog', icon: Package },
      { to: '/orders', label: 'Orders', icon: ShoppingBag },
      { to: '/bookings', label: 'Bookings', icon: CalendarCheck },
      { to: '/availability', label: 'Availability', icon: CalendarClock },
      { to: '/staff', label: 'Staff', icon: Users },
      { to: '/payments', label: 'Payments', icon: CreditCard },
      { to: '/delivery', label: 'Delivery', icon: Truck },
      { to: '/billing', label: 'Billing & charges', icon: Receipt },
    ],
  },
  {
    label: 'Engage',
    items: [
      { to: '/conversations', label: 'Conversations', icon: MessagesSquare },
      { to: '/contacts', label: 'Contacts', icon: Contact2 },
      { to: '/ai-settings', label: 'AI Settings', icon: Sparkles },
    ],
  },
  {
    label: 'Workspace',
    items: [{ to: '/settings', label: 'Settings', icon: Settings }],
  },
];

export function Sidebar({ mobile = false }: { mobile?: boolean }) {
  const savedCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const collapsed = !mobile && savedCollapsed;
  const toggle = useUIStore((s) => s.toggleSidebar);
  const setMobileOpen = useUIStore((s) => s.setMobileSidebarOpen);
  const tenantName = useAuthStore((s) => s.tenant?.name);

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-border bg-sidebar transition-[width] duration-200',
        mobile ? 'w-full' : collapsed ? 'w-[72px]' : 'w-64',
      )}
    >
      <div className={cn('flex h-20 shrink-0 items-center justify-between border-b border-border px-5', collapsed && 'justify-center px-0')}>
        <div>
          <Logo mark={collapsed} />
          {!collapsed && <p className="mt-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-mid">Tuolen for Ecommerce</p>}
        </div>
        {mobile && (
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-mid transition-colors hover:bg-accent-light hover:text-accent"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="mx-3 mb-1 mt-4 flex min-w-0 items-center gap-2.5 rounded-xl border border-border bg-white p-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-light text-accent">
            <Store aria-hidden="true" className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-primary" title={tenantName}>{tenantName || 'Your workspace'}</p>
            <p className="mt-0.5 text-[11px] text-mid">Commerce workspace</p>
          </div>
        </div>
      )}

      <nav aria-label="Merchant navigation" className="flex-1 overflow-y-auto px-3 py-3 scrollbar-thin">
        {navSections.map((section, i) => (
          <div key={section.label} className={cn(i > 0 && (collapsed ? 'mt-1.5 border-t border-sidebar-border pt-1.5' : 'mt-4'))}>
            {!collapsed && (
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-mid">
                {section.label}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    onClick={() => setMobileOpen(false)}
                    onMouseEnter={() => prefetchRoute(item.to)}
                    onFocus={() => prefetchRoute(item.to)}
                    className={({ isActive }) =>
                      cn(
                        'group relative flex min-h-10 items-center gap-2.5 rounded-[8px] px-3 py-2 text-[13px] font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1',
                        collapsed && 'justify-center px-0',
                        isActive
                          ? 'bg-accent-light font-semibold text-accent'
                          : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                      )
                    }
                    title={collapsed ? item.label : undefined}
                    aria-label={collapsed ? item.label : undefined}
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-r-full bg-accent" />
                        )}
                        <Icon aria-hidden="true" className="h-[17px] w-[17px] shrink-0" />
                        {!collapsed && <span>{item.label}</span>}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {!mobile && <div className="border-t border-border p-3">
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className={cn(
            'flex min-h-10 w-full items-center gap-2.5 rounded-[8px] px-3 py-2 text-[13px] font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1',
            collapsed && 'justify-center px-0',
          )}
        >
          {collapsed ? (
            <PanelLeftOpen aria-hidden="true" className="h-[17px] w-[17px]" />
          ) : (
            <>
              <PanelLeftClose aria-hidden="true" className="h-[17px] w-[17px]" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>}
    </aside>
  );
}
